import { format } from 'date-fns';
import { AuthenticatedHttp } from '../../http/axios';
import { parsePaginationResponse } from '../../utils/api';
import { ALL_CURRENCIES_FILTER_VALUE } from '../../utils/currency';
import { downloadFile } from '../../utils/files';
import { CACHE_TTL } from '../constants';

const reportsCache = new Map();

function normalizeValue(v) {
  if (v instanceof Date) return v.toISOString();
  if (Array.isArray(v)) return [...v].sort();
  if (v === undefined || v === null) return '';
  if (typeof v === 'object') return JSON.stringify(v);
  return v;
}

function buildKey(params) {
  const normalized = {};
  Object.keys(params || {})
    .sort()
    .forEach((k) => {
      normalized[k] = normalizeValue(params[k]);
    });
  return JSON.stringify(normalized);
}

function cachedGet(url, params, mapper) {
  const key = `${url}|${buildKey(params)}`;
  const now = Date.now();
  const cached = reportsCache.get(key);

  if (cached) {
    if (cached.response && now - cached.ts < CACHE_TTL) return Promise.resolve(cached.response);
    if (cached.promise) return cached.promise;
  }

  const promise = AuthenticatedHttp.get(url, { params })
    .then((res) => (mapper ? mapper(res) : res))
    .then((data) => {
      reportsCache.set(key, { response: data, ts: Date.now() });
      return data;
    })
    .catch((e) => {
      reportsCache.delete(key);
      throw e;
    });

  reportsCache.set(key, { promise, ts: now });
  return promise;
}

export const clearReportsCache = () => reportsCache.clear();

export function withReportCurrency(params, currency) {
  return {
    ...params,
    ...(currency && currency !== ALL_CURRENCIES_FILTER_VALUE && { currency }),
  };
}

export const getProductReport = async ({ page, search, dates, currency }) =>
  cachedGet(
    '/reports/product/',
    withReportCurrency({ page, search, ...dates }, currency),
    (res) => parsePaginationResponse(res)
  );

export const getAffiliateReport = async ({ page, search, dates, currency }) =>
  cachedGet(
    '/reports/affiliate/',
    withReportCurrency({ page, search, ...dates }, currency),
    (res) => parsePaginationResponse(res)
  );

export const getCoProductionReport = async ({ page, search, type, dates, currency }) =>
  cachedGet(
    '/reports/coproduction/',
    withReportCurrency({ page, search, type, ...dates }, currency),
    (res) => parsePaginationResponse(res)
  );

export const getBalanceReport = async ({ page, status, dates, currency }) =>
  cachedGet(
    '/reports/pending_balance/',
    withReportCurrency({ page, status, ...dates, hourly: true }, currency),
    (res) => res.data
  );

/**
 * Filtros compartilhados por listagem, exportação e métricas.
 *
 * Os três precisam enxergar exatamente o mesmo recorte: se a exportação ou as métricas
 * divergirem da tabela, o produtor vê números que não batem com o que está na tela.
 */
const buildAbandonedSalesParams = ({
  search,
  startDate,
  endDate,
  products = [],
  recoveryStatus,
}) => ({
  search,
  ...(products.length ? { products } : {}),
  ...(startDate ? { startDate: format(startDate, 'dd-MM-yyyy') } : {}),
  ...(endDate ? { endDate: format(endDate, 'dd-MM-yyyy') } : {}),
  ...(recoveryStatus ? { recoveryStatus } : {}),
});

export const getAbandonedSalesReport = async ({ page, currency, ...filters }) =>
  cachedGet(
    '/reports/checkout_abandonment/',
    withReportCurrency({ page, ...buildAbandonedSalesParams(filters) }, currency),
    (res) => parsePaginationResponse(res)
  );

export const getAbandonedSalesMetrics = async ({ currency, ...filters }) =>
  cachedGet(
    '/reports/checkout_abandonment/metrics/',
    // Sem `page`: as métricas valem para o recorte inteiro, não para a página exibida.
    withReportCurrency(buildAbandonedSalesParams(filters), currency),
    (res) => res.data
  );

export const exportAbandonedSalesReport = async ({ page, extension, currency, ...filters }) =>
  AuthenticatedHttp.get(`/reports/checkout_abandonment/export/${extension}/`, {
    responseType: 'blob',
    params: withReportCurrency({ page, ...buildAbandonedSalesParams(filters) }, currency),
  }).then((response) => {
    downloadFile({
      url: window.URL.createObjectURL(new Blob([response.data])),
      filename: `checkout_abandonment.${extension}`,
    });
  });

/**
 * Filtros do relatório de recuperação por WhatsApp.
 *
 * O período aqui recorta pela **data do disparo**, não pela do abandono: o relatório é de
 * mensagens enviadas e cobradas, e um carrinho abandonado às 23h50 com disparo às 00h10
 * cairia no dia errado se o corte fosse pelo abandono — a soma do dia não fecharia com o
 * que foi debitado do saldo.
 */
const buildWhatsappRecoveryParams = ({ search, startDate, endDate, products = [] }) => ({
  search,
  ...(products.length ? { products } : {}),
  ...(startDate ? { startDate: format(startDate, 'dd-MM-yyyy') } : {}),
  ...(endDate ? { endDate: format(endDate, 'dd-MM-yyyy') } : {}),
});

export const getWhatsappRecoveryReport = async ({ page, currency, ...filters }) =>
  cachedGet(
    '/reports/whatsapp_recovery/',
    withReportCurrency({ page, ...buildWhatsappRecoveryParams(filters) }, currency),
    (res) => parsePaginationResponse(res)
  );

export const getWhatsappRecoverySummary = async ({ currency, ...filters }) =>
  cachedGet(
    '/reports/whatsapp_recovery/summary/',
    // Sem `page`: os totais valem para o recorte inteiro, não para a página exibida.
    withReportCurrency(buildWhatsappRecoveryParams(filters), currency),
    (res) => res.data
  );

export const exportWhatsappRecoveryReport = async ({ page, extension, currency, ...filters }) =>
  AuthenticatedHttp.get(`/reports/whatsapp_recovery/export/${extension}/`, {
    responseType: 'blob',
    params: withReportCurrency({ page, ...buildWhatsappRecoveryParams(filters) }, currency),
  }).then((response) => {
    downloadFile({
      url: window.URL.createObjectURL(new Blob([response.data])),
      filename: `whatsapp_recovery.${extension}`,
    });
  });

export const exportReport = async ({
  startDate,
  endDate,
  type,
  report,
  extension,
  currency,
  ...rest
}) =>
  AuthenticatedHttp.get(`/reports/${report}/export/${extension}/`, {
    responseType: 'blob',
    params: withReportCurrency(
      {
        startDate,
        endDate,
        type,
        ...rest,
      },
      currency
    ),
  }).then((response) => {
    downloadFile({
      url: window.URL.createObjectURL(new Blob([response.data])),
      filename: `${report}_report${type ? `_${type}` : ''}.${extension}`,
    });
  });
